/**
 * Resolves the positioning variant from the URL path, persists it to
 * sessionStorage (so it survives Stripe 3DS redirects from the payment
 * page), ensures a funnel session token exists, and sets the Amplitude
 * user property so all downstream events are bucketed correctly.
 *
 * Children render only after the session is in place — no flash of
 * unauthenticated state.
 */

import {
  POSITIONING_VARIANTS,
  POSITIONING_VARIANT_STORAGE_KEY,
  type PositioningVariant,
} from '@/constants/funnel';
import { useEnsureFunnelSession } from '@/hooks/useFunnelLead';
import useAnalyticsCalls from '@/hooks/use-analytics';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

/**
 * Resolves the positioning variant from the pathname. URL shape is
 * /join[/<variant>][/<step>], so the variant — if present — is always the
 * second segment.
 */
function resolveVariantFromPath(pathname: string): PositioningVariant {
  const segments = pathname.replace(/^\/+|\/+$/g, '').split('/');
  if (segments[0] !== 'join') return 'default';
  const candidate = segments[1];
  if (candidate && (POSITIONING_VARIANTS as readonly string[]).includes(candidate)) {
    return candidate as PositioningVariant;
  }
  return 'default';
}

interface FunnelGuardProps {
  children?: ReactNode;
}

export function FunnelGuard({ children }: FunnelGuardProps) {
  const location = useLocation();
  const variant = useMemo(() => resolveVariantFromPath(location.pathname), [location.pathname]);

  // Persist for 3DS redirect survival on the payment page.
  useEffect(() => {
    sessionStorage.setItem(POSITIONING_VARIANT_STORAGE_KEY, variant);
  }, [variant]);

  // Mint or reuse the funnel session — single network call on first mount.
  const { hasSession, isInitializing } = useEnsureFunnelSession(variant);

  // Set the Amplitude user property exactly once per session.
  const { setPositioningVariantProperty } = useAnalyticsCalls();
  const hasSetPropRef = useRef(false);
  useEffect(() => {
    if (!hasSetPropRef.current) {
      hasSetPropRef.current = true;
      setPositioningVariantProperty(variant);
    }
  }, [variant, setPositioningVariantProperty]);

  if (isInitializing || !hasSession) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-catch-tan">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-catch-dark-blue-90 border-t-transparent" />
      </div>
    );
  }

  return <>{children ?? <Outlet />}</>;
}
